import React, { useCallback, useState, useMemo, useEffect } from "react";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import path from "path-browserify";
import { useTranslation } from "react-i18next";

import Creative from "./photo-editor-creative/PhotoEditorCreative";
import CampaignCreateMediaLibrary from "../campaign-create-media-library/CampaignCreateMediaLibrary";
import InlineImageCropper from "../InlineImageCropper";
import { MutedIcon, VolumeIcon, PlayIcon, PauseIcon } from "../Svg";
import { SHUTTERSTOCK_MAX_IMAGES } from "../../../../constants";

import {
  isVideo,
  getCloudinaryLinkWithoutCloudinaryCrop,
  getCloudinaryCropParamsFromURL,
  removeShutterstockTransformations
} from "../utils";

import { CustomerSucessController, Store__System } from "@talentbait/shared-components";

import * as SC from "./CampaignCreatePhotoEditor.styled";

import { resizeImage } from "../../../../utils/images";
import { withRefluxStore, PreviewStore } from "../../../../Stores";
import useComponentVisible from "../../../../hooks/useComponentVisible";
import Actions from "../../../../Actions";
import logger from "../../../../utils/LoggerFrontend";
import useArchivePicture from "../../../../hooks/useArchivePicture";
import { Tooltip } from "../../../../Components/Overlays";

const CampaignCreatePhotoEditor = ({
  orgaId,
  postDataKey = "creatives",
  creatives,
  creativeAssets = [],
  logoAssets = [],
  selectedCreativeIndexToEdit,
  updatePost,
  minHeight,
  maxHeight,
  minAspect,
  maxAspect,
  extraAspectRatios,
  channelMinHeight,
  channelMinWidth,
  width,
  borderRadius = "0",
  allowVideos,
  allowImages,
  editable,
  canBuyStockPicture = false,
  isLogo = false,
  // Store
  campaignData,
  userProfile
}) => {
  const { t } = useTranslation();
  const { companyId } = campaignData || {};

  const creative = useMemo(
    () => resizeImage({ url: creatives[selectedCreativeIndexToEdit] || creatives[0] || "" }),
    [creatives, selectedCreativeIndexToEdit]
  );

  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const atVideo = useMemo(() => {
    if (!isLogo && creative) {
      return isVideo(creative);
    }
    return false;
  }, [creative, isLogo]);

  const toggleMute = useCallback(() => setMuted((muted) => !muted), []);
  const togglePlaying = useCallback(() => setPlaying((playing) => !playing), []);

  //// Media Library states ////
  const savedCreativeWithoutCrop = useMemo(
    () => getCloudinaryLinkWithoutCloudinaryCrop(creative),
    [creative]
  );

  const savedCreativesWithoutCrop = useMemo(
    () => creatives.map((creative) => getCloudinaryLinkWithoutCloudinaryCrop(creative)),
    [creatives]
  );

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedCreativePreview, setSelectedCreativePreview] = useState(savedCreativeWithoutCrop);
  const [croppedCreativePreview, setCroppedCreativePreview] = useState(creative);

  useEffect(() => {
    setSelectedCreativePreview(savedCreativeWithoutCrop);
  }, [savedCreativeWithoutCrop]);

  useEffect(() => {
    setCroppedCreativePreview(creative);
  }, [creative]);

  const updateSelectedCreative = useCallback((creative) => {
    setSelectedCreativePreview(creative);
    setCroppedCreativePreview(creative);
  }, []);

  const updateSelectedAsset = (asset) => {
    setSelectedAsset(asset);

    logger.log("Campaign__Create_PhotoEditor", "updateSelectedAsset", {
      ...asset,
      ...campaignData,
      userProfile
    });

    // Campaign data for segment tracking purposes. Fetching it from the Preview store
    const { id: campaignId, orgaName: companyName, jobTitle } = campaignData || {};
    // Segment tracking
    CustomerSucessController.send("Image Selected", {
      campaignId,
      orgaId,
      companyName,
      jobTitle,
      ...asset
    });
  };

  // Used to detect which type of orientation does Shutterstock images have
  const [selectedCreativeOrientation, setSelectedCreativeOrientation] = useState("landscape");

  const updateShutterstockPostHandler = useCallback(
    (assetId) => {
      const newCreatives = [...creatives];
      let assetObjects = [];
      let assetKey;

      if (newCreatives.length > SHUTTERSTOCK_MAX_IMAGES) {
        Actions.addNotification(
          t(
            "You can add a maximum of {{limit}} images. To use a different image, remove an already selected one.",
            { limit: SHUTTERSTOCK_MAX_IMAGES }
          ),
          "warning"
        );
        return;
      }

      if (postDataKey === "creatives") {
        assetKey = "creativeAssets";
        assetObjects = [...creativeAssets];
      } else if (postDataKey === "logos") {
        assetKey = "logoAssets";
        assetObjects = [...logoAssets];
      }

      newCreatives[selectedCreativeIndexToEdit] = croppedCreativePreview;
      const creativesWithoutTransformations = newCreatives.map(removeShutterstockTransformations);

      if (assetId) {
        const index = assetObjects.map((asset) => asset.id).indexOf(assetId);
        if (index >= 0) {
          assetObjects[index] = {
            id: assetId,
            orientation: selectedCreativeOrientation
          };
        } else {
          assetObjects.push({
            id: assetId,
            orientation: selectedCreativeOrientation
          });
        }
      }

      Actions.getAssets(assetObjects.map((asset) => asset.id));

      const ref = Actions.getAssets.completed.listen((result) => {
        let assets = result.meta.payload;

        // 1. We remove the assets we are not using anymore
        assets = assets.filter(
          (asset) =>
            creativesWithoutTransformations.includes(asset.url) ||
            newCreatives.includes(asset.shutterstock?.unlicensedUrl)
        );

        // 2. We select the attributes that we want to store to creativeAssets
        assets = assets.map((asset) => ({
          id: asset.id,
          orientation: assetObjects.find((assetObject) => asset.id === assetObject.id).orientation
        }));

        updatePost({
          [postDataKey]: newCreatives,
          [assetKey]: assets
        });
        ref();
      });

      const refFailed = Actions.getAssets.failed.listen(() => {
        updatePost({
          [postDataKey]: newCreatives,
          [assetKey]: assetObjects
        });
        refFailed();
      });
    },
    [
      creativeAssets,
      creatives,
      croppedCreativePreview,
      selectedCreativeOrientation,
      logoAssets,
      postDataKey,
      selectedCreativeIndexToEdit,
      t,
      updatePost
    ]
  );

  const updatePostHandler = useCallback(() => {
    const newCreatives = [...creatives];
    newCreatives[selectedCreativeIndexToEdit] = croppedCreativePreview;
    const creativesWithoutTransformations = newCreatives.map(removeShutterstockTransformations);

    let assetObjects = [];
    let assetKey;

    if (postDataKey === "creatives") {
      assetKey = "creativeAssets";
      assetObjects = [...creativeAssets];
    } else if (postDataKey === "logos") {
      assetKey = "logoAssets";
      assetObjects = [...logoAssets];
    }

    Actions.getAssetsPhotoEditor(assetObjects.map((asset) => asset.id));

    const ref = Actions.getAssetsPhotoEditor.completed.listen((result) => {
      let assets = result.meta.payload;

      // We remove the assets we are not using anymore
      assets = assets.filter(
        (asset) =>
          creativesWithoutTransformations.includes(asset.url) ||
          newCreatives.includes(asset.shutterstock?.unlicensedUrl)
      );

      // We select the attributes that we want to store to creativeAssets
      assets = assets.map((asset) => ({
        id: asset.id,
        orientation: assetObjects.find((assetObject) => asset.id === assetObject.id).orientation
      }));

      updatePost({
        [postDataKey]: newCreatives,
        [assetKey]: assets
      });

      ref();
    });

    const refFailed = Actions.getAssetsPhotoEditor.failed.listen(() => {
      updatePost({
        [postDataKey]: newCreatives
      });

      refFailed();
    });
  }, [
    croppedCreativePreview,
    creatives,
    selectedCreativeIndexToEdit,
    postDataKey,
    updatePost,
    creativeAssets,
    logoAssets
  ]);

  // Opening, closing and saving logic
  const closeAndSaveCallback = useCallback(() => {
    if (selectedAsset) {
      // Shutterstock use case
      const extension = path.extname(selectedAsset.url);

      const data = {
        externalId: selectedAsset.imageId,
        searchId: selectedAsset.searchId,
        url: selectedAsset.url,
        source: selectedAsset.source,
        assetType: "creative",
        mediaType: "image",
        extension
      };

      Actions.saveAssets(orgaId, companyId, [data]);

      logger.log("Campaign__Create_PhotoEditor", "closeAndSaveCallback post editor", {
        ...data,
        ...campaignData
      });

      // Campaign data for segment tracking purposes. Fetching it from the Preview store
      const {
        id: campaignId,
        orgaName: companyName,
        organizationId,
        jobTitle
      } = campaignData || {};
      const { userName } = userProfile || {};

      // Segment tracking
      CustomerSucessController.send("Image Applied", {
        origin: "PhotoEditor",
        campaignId,
        orgaId: organizationId,
        companyName,
        jobTitle,
        userName,
        ...data
      });

      const ref = Actions.saveAssets.completed.listen((result) => {
        const assetId = result.meta.payload[0];
        updateShutterstockPostHandler(assetId);
        ref();
      });

      const refFailed = Actions.saveAssets.failed.listen(() => {
        updateShutterstockPostHandler();
        refFailed();
      });
    } else {
      // Non shutterstock use case
      updatePostHandler();
    }
  }, [
    userProfile,
    campaignData,
    orgaId,
    selectedAsset,
    updatePostHandler,
    updateShutterstockPostHandler
  ]);

  const {
    ref: mediaLibraryRef,
    isComponentVisible: isMediaLibraryOpen,
    setIsComponentVisible: setIsMediaLibraryOpen
  } = useComponentVisible(false, closeAndSaveCallback);

  const closeMediaLibrary = useCallback(() => {
    setIsMediaLibraryOpen(false);
    setSelectedCreativePreview(savedCreativeWithoutCrop);
    setCroppedCreativePreview(creative);
  }, [creative, savedCreativeWithoutCrop, setIsMediaLibraryOpen]);

  const openMediaLibrary = useCallback(() => setIsMediaLibraryOpen(true), [setIsMediaLibraryOpen]);

  const saveSelected = useCallback(() => {
    setIsMediaLibraryOpen(false);
    closeAndSaveCallback();
  }, [closeAndSaveCallback, setIsMediaLibraryOpen]);

  // only use cropper if image is cloudinary
  const useCropper = useMemo(
    () =>
      croppedCreativePreview.startsWith("https://res.cloudinary.com/") ||
      croppedCreativePreview.startsWith("https://image.shutterstock.com/"),
    [croppedCreativePreview]
  );

  // get default crop params from creative
  const { creativeWithoutCrop, cropFromCreative } = useMemo(
    () =>
      useCropper
        ? {
            creativeWithoutCrop: getCloudinaryLinkWithoutCloudinaryCrop(croppedCreativePreview),
            cropFromCreative: getCloudinaryCropParamsFromURL(croppedCreativePreview)
          }
        : {},
    [croppedCreativePreview, useCropper]
  );

  const isArchivable = (url) => {
    const creativePreview = getCloudinaryLinkWithoutCloudinaryCrop(croppedCreativePreview);

    return (
      selectedAsset?.url !== url &&
      creativePreview !== url &&
      !savedCreativesWithoutCrop.includes(url)
    );
  };

  const { archivePictureCallback } = useArchivePicture(campaignData, userProfile);

  return (
    <SC.EditorWrapper>
      <div ref={mediaLibraryRef}>
        <SC.MediaActions active={atVideo}>
          <SC.MuteButton onClick={toggleMute}>
            {muted ? <MutedIcon /> : <VolumeIcon />}
          </SC.MuteButton>
          <SC.PlayingButton onClick={togglePlaying}>
            {playing ? <PauseIcon /> : <PlayIcon />}
          </SC.PlayingButton>
        </SC.MediaActions>

        {isMediaLibraryOpen ? (
          <SwitchTransition>
            <CSSTransition
              key={useCropper ? `cropper_${creativeWithoutCrop}` : croppedCreativePreview}
              addEndListener={(node, done) => node.addEventListener("transitionend", done, false)}
              classNames="pictureFader"
            >
              {useCropper ? (
                <InlineImageCropper
                  creativeWithoutCrop={creativeWithoutCrop}
                  cropFromCreative={cropFromCreative}
                  setCroppedCreative={setCroppedCreativePreview}
                  updateSelectedCreativeOrientation={setSelectedCreativeOrientation}
                  minAspect={minAspect}
                  maxAspect={maxAspect}
                  minHeight={minHeight}
                  maxHeight={maxHeight}
                  channelMinHeight={channelMinHeight}
                  channelMinWidth={channelMinWidth}
                  width={width}
                  extraAspectRatios={extraAspectRatios}
                  isLogo={isLogo}
                />
              ) : (
                <SC.CreativeWrapper
                  width={width}
                  active
                  minHeight={minHeight}
                  maxHeight={maxHeight}
                >
                  <Creative url={croppedCreativePreview} playing muted={muted} />
                </SC.CreativeWrapper>
              )}
            </CSSTransition>
          </SwitchTransition>
        ) : (
          <SC.CreativeAndUploaderWrapper style={{ borderRadius }}>
            <SC.CreativeWrapper
              key={creative}
              width={width}
              minHeight={minHeight}
              maxHeight={maxHeight}
            >
              <Creative url={creative} playing={playing} muted={muted} />
            </SC.CreativeWrapper>

            {editable && (
              <SC.UploaderWrapper>
                <Tooltip label="Neues Bild hochladen">
                  <SC.MediaLibraryOpenButton onClick={openMediaLibrary}>
                    <SC.UploadCloudIcon src="/images/upload_cloud.svg" small={isLogo} alt="" />
                  </SC.MediaLibraryOpenButton>
                </Tooltip>
              </SC.UploaderWrapper>
            )}
          </SC.CreativeAndUploaderWrapper>
        )}

        {editable && (
          <CampaignCreateMediaLibrary
            cancelSelected={closeMediaLibrary}
            saveSelected={saveSelected}
            isMediaLibraryOpen={isMediaLibraryOpen}
            selectedCreative={selectedCreativePreview}
            updateSelectedCreative={updateSelectedCreative}
            updateSelectedAsset={updateSelectedAsset}
            archivePicture={archivePictureCallback}
            isArchivable={isArchivable}
            minAspect={minAspect}
            maxAspect={maxAspect}
            channelMinHeight={channelMinHeight}
            channelMinWidth={channelMinWidth}
            disableStock={isLogo}
            allowVideos={allowVideos}
            allowImages={allowImages}
            canBuyStockPicture={canBuyStockPicture}
            canBeEmpty={isLogo}
            isLogo={isLogo}
          />
        )}
      </div>
    </SC.EditorWrapper>
  );
};

export default withRefluxStore(
  CampaignCreatePhotoEditor,
  [PreviewStore, Store__System],
  ["campaignData", "userProfile"]
);
